import React, { useState, useEffect } from 'react'
import { Box, Typography, InputAdornment, Icon, RadioGroup } from '@mui/material'
import { Field, ErrorMessage } from 'formik'
import { useQuery } from 'react-query'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxCheckbox from 'components/custom/AdxCheckbox'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxRadioButton from 'components/custom/AdxRadioButton'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { getLeavePolicies } from 'services/api/modules/leave/leavePolicy'

const PolicyAdvancedSetting = ({ formikBag, type, policy }) => {
  const { values, handleChange, setFieldValue, errors, touched, setFieldTouched } = formikBag
  const [carryForwardAmount, setCarryForwardAmount] = useState('')
  const [carryForwardExpiry, setCarryForwardExpiry] = useState('')

  const { data: leavePolicyOptions } = useQuery('leave-policy', async () => {
    const { data: apiData } = await getLeavePolicies()
    return apiData.data
  })

  const deductFromPolicyOptions = (leavePolicyOptions?.with_entitlement || []).filter(
    (item) => item.id !== policy?.id
  )

  useEffect(() => {
    if (values.checkboxes.carryForward && values.carry_forward_amount) {
      if (values.carry_forward_amount === -1) {
        setCarryForwardAmount(-1)
      } else {
        setCarryForwardAmount(0)
      }
    }
    if (values.checkboxes.carryForward && values.carry_forward_expiry_period) {
      if (values.carry_forward_expiry_period === 'cycle_period') {
        setCarryForwardExpiry(-1)
      } else if (values.carry_forward_expiry_period === 'no_expiry') {
        setCarryForwardExpiry(-2)
      } else {
        setCarryForwardExpiry(0)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box display="flex" flexDirection="column" rowGap={4}>
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
        {type === 'WITHOUT' ? null : (
          <>
            <AdxFormControlLabel
              type="no-fill"
              label="Enable leave application quota"
              description="Enable to limit how many employees can apply in a day"
              control={
                <AdxCheckbox
                  as={AdxCheckbox}
                  name="quotaCheckbox"
                  checked={values.checkboxes.quota}
                  onChange={(e) => {
                    setFieldValue('checkboxes.quota', e.target.checked)
                  }}
                />
              }
            />

            {values.checkboxes.quota && (
              <>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      pl: 6.5,
                    }}
                  >
                    <Box
                      width={{ xs: 1, lg: 1 / 2 }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        columnGap: 2,
                      }}
                    >
                      <Field
                        as={AdxInput}
                        label="Daily Quota"
                        name="quota_amount"
                        // value={values.quota_amount}
                        onChange={handleChange}
                        type="number"
                        isErrorShown={errors.quota_amount}
                        sx={{ width: 1 / 2 }}
                      />
                      <Field
                        as={AdxDropdown}
                        label="Unit"
                        name="quota_unit"
                        value={values.quota_unit}
                        onChange={handleChange}
                        sx={{ width: 1 / 2 }}
                        isErrorShown={errors.quota_unit}
                      >
                        <AdxMenuItem selectedvalue={values.quota_unit} value="percent">
                          Percent
                        </AdxMenuItem>
                        <AdxMenuItem selectedvalue={values.quota_unit} value="number">
                          People
                        </AdxMenuItem>
                      </Field>
                    </Box>
                    <Typography
                      variant="body1"
                      color="dark-text.main"
                      sx={{
                        mx: { xs: 0, sm: 2 },
                        my: { xs: 1, sm: 0 },
                        mt: { xs: 1.5, sm: 2.5 },
                        textAlign: 'center',
                        width: { xs: 1, sm: 'initial' },
                      }}
                    >
                      per
                    </Typography>
                    <Field
                      as={AdxDropdown}
                      label="Category"
                      name="quota_category"
                      value={values.quota_category}
                      onChange={handleChange}
                      sx={{ width: { xs: 1, lg: 1 / 4 } }}
                      isErrorShown={errors.quota_category}
                    >
                      <AdxMenuItem selectedvalue={values.quota_category} value="department">
                        Department
                      </AdxMenuItem>
                      <AdxMenuItem selectedvalue={values.quota_category} value="company">
                        Company
                      </AdxMenuItem>
                    </Field>
                  </Box>
                  {(errors.quota_amount || errors.quota_unit || errors.quota_category) && (
                    <Box pl={6.5}>
                      <AdxErrorMessage>All fields are required</AdxErrorMessage>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}
        {type === 'WITHOUT' ? null : (
          <>
            <AdxFormControlLabel
              type="no-fill"
              label="Deduct balance from another leave policy"
              description="Enable to deduct requests under this policy from another policy's entitlement balance instead of its own (e.g. Emergency Leave deducting from Annual Leave)."
              control={
                <AdxCheckbox
                  as={AdxCheckbox}
                  name="deductFromPolicyCheckbox"
                  checked={values.checkboxes.deductFromPolicy}
                  onChange={(e) => {
                    setFieldValue('checkboxes.deductFromPolicy', e.target.checked)
                    if (!e.target.checked) {
                      setFieldValue('deduct_from_leave_policy_id', '')
                    }
                  }}
                />
              }
            />
            {values.checkboxes.deductFromPolicy && (
              <Box pl={6.5}>
                <Field
                  as={AdxDropdown}
                  label="Deduct balance from"
                  name="deduct_from_leave_policy_id"
                  value={values.deduct_from_leave_policy_id}
                  onChange={handleChange}
                  sx={{ width: { xs: 1, sm: 1 / 2 } }}
                  isErrorShown={errors.deduct_from_leave_policy_id}
                >
                  {deductFromPolicyOptions.map((item) => (
                    <AdxMenuItem
                      key={item.id}
                      selectedvalue={values.deduct_from_leave_policy_id}
                      value={item.id}
                    >
                      {item.name}
                    </AdxMenuItem>
                  ))}
                </Field>
                {errors.deduct_from_leave_policy_id && (
                  <AdxErrorMessage>{errors.deduct_from_leave_policy_id}</AdxErrorMessage>
                )}
              </Box>
            )}
          </>
        )}
        <AdxFormControlLabel
          type="no-fill"
          label="Enable application restriction"
          description="Enable to limit how many consecutive days an employee may be on leave"
          control={
            <AdxCheckbox
              as={AdxCheckbox}
              name="restrictionCheckbox"
              checked={values.checkboxes.restriction}
              onChange={(e) => setFieldValue('checkboxes.restriction', e.target.checked)}
            />
          }
        />
        {values.checkboxes.restriction && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              pl: 6.5,
            }}
          >
            <Field
              as={AdxInput}
              label="Restriction"
              name="restriction_amount"
              // value={values.restriction_amount}
              onChange={handleChange}
              type="number"
              errorMessage={<ErrorMessage name="restriction_amount" component={AdxErrorMessage} />}
              isErrorShown={errors.restriction_amount && touched.restriction_amount}
              sx={{ width: { xs: 1, sm: 1 / 4 } }}
              endAdornment={
                <InputAdornment position="end">
                  <Typography color="light-gray-text.main">days</Typography>
                </InputAdornment>
              }
            />
            <Typography
              variant="body1"
              color="dark-text.main"
              sx={{
                mx: { xs: 0, sm: 2 },
                mt:
                  errors.restriction_amount && touched.restriction_amount
                    ? null
                    : { xs: 0, sm: 2.5 },
              }}
            >
              in a row (maximum)
            </Typography>
          </Box>
        )}
        <AdxFormControlLabel
          type="no-fill"
          label="Enable minimum date for leave request"
          description="Enable to set number of days required prior to applying leave."
          control={
            <AdxCheckbox
              as={AdxCheckbox}
              name="minDateCheckbox"
              checked={values.checkboxes.minDate}
              onChange={(e) => setFieldValue('checkboxes.minDate', e.target.checked)}
            />
          }
        />
        {values.checkboxes.minDate && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              pl: 6.5,
            }}
          >
            <Field
              as={AdxInput}
              label="Min. Date Prior"
              name="day_prior"
              // value={values.day_prior}
              onChange={handleChange}
              type="number"
              errorMessage={<ErrorMessage name="day_prior" component={AdxErrorMessage} />}
              isErrorShown={errors.day_prior && touched.day_prior}
              sx={{ width: { xs: 1, sm: 1 / 4 } }}
              endAdornment={
                <InputAdornment position="end">
                  <Typography color="light-gray-text.main">days</Typography>
                </InputAdornment>
              }
            />
            <Typography
              variant="body1"
              color="dark-text.main"
              sx={{
                mx: { xs: 0, sm: 2 },
                mt: errors.day_prior && touched.day_prior ? null : { xs: 0, sm: 2.5 },
              }}
            >
              from today
            </Typography>
          </Box>
        )}

        {values.cycle_period === 'monthly' || type === 'WITHOUT' ? null : (
          <>
            <AdxFormControlLabel
              type="no-fill"
              label="Allow carry forward"
              description="Enable to allow unused leaves to be carried-forward to the following year."
              control={
                <AdxCheckbox
                  as={AdxCheckbox}
                  name="carryForwardCheckbox"
                  checked={values.checkboxes.carryForward}
                  onChange={(e) => {
                    setFieldValue('checkboxes.carryForward', e.target.checked)
                    setFieldTouched('carry_forward_amount')
                  }}
                />
              }
            />
            {values.checkboxes.carryForward && (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', pl: 6.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
                    <Typography>Number of days for leave carry forward policy</Typography>
                    <Icon fontSize="inherit" sx={{ color: 'information.main' }}>
                      info
                    </Icon>
                  </Box>
                  <RadioGroup
                    sx={{ width: '100%' }}
                    name="carryForwardAmount"
                    value={carryForwardAmount}
                    onChange={(event) => {
                      setCarryForwardAmount(Number(event.target.value))
                      if (Number(event.target.value) === -1) {
                        setFieldValue('carry_forward_amount', -1)
                      }
                      if (Number(event.target.value) === 0) {
                        setFieldValue('carry_forward_amount', 0)
                      }
                    }}
                  >
                    <AdxFormControlLabel
                      type="no-fill"
                      selected={carryForwardAmount}
                      value={-1}
                      control={<AdxRadioButton />}
                      label="Unlimited"
                    />
                    <AdxFormControlLabel
                      type="no-fill"
                      selected={carryForwardAmount}
                      value={0}
                      control={<AdxRadioButton />}
                      label="Up to"
                    />
                  </RadioGroup>

                  {carryForwardAmount === '' ? (
                    <ErrorMessage name="carry_forward_amount" component={AdxErrorMessage} />
                  ) : null}

                  {carryForwardAmount === 0 && (
                    <Field
                      as={AdxInput}
                      name="carry_forward_amount"
                      // value={values.carry_forward_amount}
                      onChange={handleChange}
                      type="number"
                      errorMessage={
                        <ErrorMessage name="carry_forward_amount" component={AdxErrorMessage} />
                      }
                      isErrorShown={errors.carry_forward_amount && touched.carry_forward_amount}
                      sx={{ width: 1 / 6, ml: 5.25 }}
                      inputProps={{
                        min: 0,
                      }}
                      endAdornment={
                        <InputAdornment position="end">
                          <Typography color="light-gray-text.main">days</Typography>
                        </InputAdornment>
                      }
                    />
                  )}
                  <Typography>Carry forward expiry</Typography>
                  <RadioGroup
                    sx={{ width: '100%' }}
                    name="carryForwardExpiry"
                    value={carryForwardExpiry}
                    onChange={(event) => {
                      setCarryForwardExpiry(Number(event.target.value))
                      if (Number(event.target.value) === -1) {
                        setFieldValue('carry_forward_expiry_period', 'cycle_period')
                      }
                      if (Number(event.target.value) === -2) {
                        setFieldValue('carry_forward_expiry_period', 'no_expiry')
                      }
                      if (Number(event.target.value) === 0) {
                        setFieldValue('carry_forward_expiry_period', 'day')
                        setFieldValue('carry_forward_expiry_amount', 0)
                      }
                    }}
                  >
                    <AdxFormControlLabel
                      type="no-fill"
                      selected={carryForwardExpiry}
                      value={-1}
                      control={<AdxRadioButton />}
                      label="End of cycle period"
                    />
                    <AdxFormControlLabel
                      type="no-fill"
                      selected={carryForwardExpiry}
                      value={-2}
                      control={<AdxRadioButton />}
                      label="No expiry"
                    />
                    <AdxFormControlLabel
                      type="no-fill"
                      selected={carryForwardExpiry}
                      value={0}
                      control={<AdxRadioButton />}
                      label="Custom"
                    />
                  </RadioGroup>
                  {carryForwardExpiry === 0 && (
                    <>
                      <Box
                        pl={4}
                        sx={{
                          width: 1,
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: null, sm: 'center' },
                        }}
                      >
                        <Box
                          display={'flex'}
                          flexDirection={'row'}
                          alignItems={'center'}
                          columnGap={2}
                        >
                          <Field
                            as={AdxInput}
                            name="carry_forward_expiry_amount"
                            // value={values.carry_forward_expiry_amount}
                            onChange={handleChange}
                            type="number"
                            sx={{ width: 1 / 2 }}
                            isErrorShown={errors.carry_forward_expiry_amount}
                          />
                          <Field
                            as={AdxDropdown}
                            name="carry_forward_expiry_period"
                            value={values?.carry_forward_expiry_period}
                            onChange={handleChange}
                            sx={{ width: 1 / 2 }}
                            isErrorShown={errors.carry_forward_expiry_period}
                          >
                            <AdxMenuItem
                              value="day"
                              selectedvalue={values.carry_forward_expiry_period}
                            >
                              Days
                            </AdxMenuItem>
                            <AdxMenuItem
                              value="week"
                              selectedvalue={values.carry_forward_expiry_period}
                            >
                              Weeks
                            </AdxMenuItem>
                            <AdxMenuItem
                              value="month"
                              selectedvalue={values.carry_forward_expiry_period}
                            >
                              Months
                            </AdxMenuItem>
                          </Field>
                        </Box>

                        <Box sx={{ mx: { xs: null, sm: 1 } }}>
                          <Typography variant="subtitle1">after new cycle period</Typography>
                        </Box>
                      </Box>
                      <Box ml={4}>
                        <ErrorMessage
                          name="carry_forward_expiry_amount"
                          component={AdxErrorMessage}
                        />

                        <ErrorMessage
                          name="carry_forward_expiry_period"
                          component={AdxErrorMessage}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export default PolicyAdvancedSetting
